import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import { getGuideAccent } from "../../utils/guideAccent";

// Template340 (Edimburgo): Harry Potter — Balmoral, George Heriot’s y tour
const Template340 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;
	const introText = page.texts?.[1]?.content;
	const sections = Array.isArray(page?.sections) ? page.sections : [];

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const actualHeaderColor = headerBackgroundColor || "#de5624";
	const accent = getGuideAccent(actualHeaderColor);
	const contentColor = textColor || "#444";

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
		marginBottom: "4px",
		color: accent.accent,
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "12px",
		color: contentColor,
		margin: 0,
		lineHeight: 1.35,
	};

	const linkStyle = {
		color: accent.accent,
		fontWeight: 700,
		textDecoration: "underline",
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{logoImage && !isSmallScreen && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{introText && (
				<Box data-block-id="text-1" sx={{ padding: "10px 16px 6px" }}>
					<Box component="p" sx={estiloP}>
						{introText}
					</Box>
				</Box>
			)}

			<Box
				sx={{
					padding: "0 16px 14px",
					display: "flex",
					flexDirection: "column",
					gap: "8px",
				}}
			>
				{sections.map((section, idx) => {
					const imageSrc = getImageSrc(section.imageId);
					const isYellow = section.layout === "yellow";
					const isHighlight = section.layout === "highlight";
					const isBox = isYellow || isHighlight || section.layout === "box";

					if (isBox) {
						return (
							<Box key={idx} data-block-id={`text-${idx + 2}`}>
								<Box
									sx={{
										backgroundColor: isYellow
											? "#fff8db"
											: isHighlight
												? "#f7fafc"
												: accent.boxBg,
										border: isYellow
											? "2px solid #f5c400"
											: isHighlight
												? `2px solid ${accent.boxBorder}`
												: "none",
										borderRadius: "12px",
										padding: "8px 10px",
									}}
								>
									{section.title && (
										<Box component="h3" sx={estiloH3}>
											{section.title}
										</Box>
									)}
									{section.body && (
										<Box component="p" sx={estiloP}>
											{section.body}{" "}
											{section.reserveLink?.url && (
												<a
													href={section.reserveLink.url}
													target="_blank"
													rel="noopener noreferrer"
													style={linkStyle}
												>
													{section.reserveLink.text}
												</a>
											)}
										</Box>
									)}
								</Box>
							</Box>
						);
					}

					return (
						<Box
							key={idx}
							data-block-id={`text-${idx + 2}`}
							sx={{
								position: "relative",
								display: "flex",
								gap: "12px",
								alignItems: "flex-start",
							}}
						>
							{imageSrc && (
								<Box
									sx={{
										width: "38%",
										flexShrink: 0,
										lineHeight: 0,
									}}
								>
									<img
										src={imageSrc}
										alt={section.title}
										style={{
											width: "100%",
											height: 88,
											objectFit: "cover",
											borderRadius: "12px",
											display: "block",
										}}
									/>
								</Box>
							)}
							<Box sx={{ width: imageSrc ? "62%" : "100%" }}>
								{section.title && (
									<Box component="h3" sx={estiloH3}>
										{section.title}
									</Box>
								)}
								{section.body && (
									<Box component="p" sx={estiloP}>
										{section.body}
									</Box>
								)}
							</Box>
							{section.sabiasQue?.map((sq, i) => (
								<Box
									key={i}
									sx={{
										position: "absolute",
										top: -8 + i * 36,
										left: imageSrc ? "calc(38% - 18px)" : 0,
									}}
								>
									<SabiasQue icono={sq.icono} texto={sq.texto} />
								</Box>
							))}
						</Box>
					);
				})}
			</Box>
		</Box>
	);
};

Template340.thumbnail = "/images/template-1.png";

export default Template340;
